// frontend/src/context/AuthContext.js
import React, { createContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';

// Definiere die API_URL hier, indem du das /api-Präfix hinzufügst
// process.env.REACT_APP_BACKEND_URL sollte nur die Basis-URL sein (z.B. https://dein-backend.com)
// WICHTIG: In Produktion IMMER HTTPS verwenden!

// API URL Konfiguration
const getApiUrl = () => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  // Wenn Backend-URL gesetzt ist, verwende sie
  if (backendUrl) {
    // Warnung ausgeben wenn HTTP in Produktion verwendet wird
    if (process.env.NODE_ENV === 'production' && backendUrl.startsWith('http://')) {
      console.warn('⚠️  SICHERHEITSWARNUNG: Backend-URL verwendet HTTP statt HTTPS in Produktion!');
    }

    // Verhindere doppeltes /
    let cleanUrl = backendUrl.endsWith('/') ? backendUrl.slice(0, -1) : backendUrl;
    
    // Sicherstellen, dass /api angehängt wird, wenn es keine direkte Worker-Domain ist
    if (!cleanUrl.endsWith('/api') && !cleanUrl.includes('.workers.dev')) {
      cleanUrl = `${cleanUrl}/api`;
    }
    
    return cleanUrl;
  }

  // Fallback für Entwicklung
  if (process.env.NODE_ENV !== 'production') {
    return 'http://localhost:5000/api';
  }

  return '/api'; // Relativer Pfad als letzter Ausweg
};

const API_URL = getApiUrl();
const rawPublicUrl = process.env.REACT_APP_PUBLIC_API_URL || API_URL;
let PUBLIC_API_URL = rawPublicUrl.endsWith('/') ? rawPublicUrl.slice(0, -1) : rawPublicUrl;

// Auch bei PUBLIC_API_URL sicherstellen, dass /api angehängt ist
if (!PUBLIC_API_URL.endsWith('/api')) {
  PUBLIC_API_URL = `${PUBLIC_API_URL}/api`;
}

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Bob page context - pages can inject hidden info for Bob here
  const [bobContext, setBobContext] = useState(null);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      const parsedUser = JSON.parse(storedUser);
      if (parsedUser.token) {
        try {
          const decoded = jwtDecode(parsedUser.token);
          if (decoded.exp * 1000 < Date.now()) {
            // Token is expired
            localStorage.removeItem('user');
            localStorage.removeItem('userInfo');
            setUser(null);
          } else {
            setUser(parsedUser);
          }
        } catch (e) {
          // Invalid token
          localStorage.removeItem('user');
          localStorage.removeItem('userInfo');
          setUser(null);
        }
      } else {
        setUser(parsedUser);
      }
    }
    setLoading(false);
  }, []);

  const login = useCallback(async (email, password, recaptchaToken) => {
    try {
      const config = {
        headers: {
          'Content-Type': 'application/json',
        },
      };
      // Nutze den Cloudflare Worker für den Login
      const res = await axios.post(`${PUBLIC_API_URL}/users/login`, { email, password, recaptchaToken }, config);
      setUser(res.data);
      localStorage.setItem('user', JSON.stringify(res.data));
      return true;
    } catch (error) {
      console.error('Login failed:', error.response ? error.response.data.message : error.message);
      setUser(null); // Sicherstellen, dass der Benutzerstatus zurückgesetzt wird
      // NEU: Wirf einen Fehler mit der spezifischen Nachricht vom Backend
      if (error.response && error.response.data && error.response.data.message) {
        throw new Error(error.response.data.message);
      } else if (error.message) {
        throw new Error(error.message);
      } else {
        throw new Error('Unbekannter Login-Fehler.');
      }
    }
  }, [API_URL]);

  const googleLogin = useCallback((token) => {
    try {
      const decoded = jwtDecode(token);
      const userData = { token, ...decoded };
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
    } catch (error) {
      console.error('Google login failed:', error);
      throw error;
    }
  }, []);

  const registerUser = useCallback(async (email, password, registrationCode, userData, avatarUrl) => {
    try {
      const config = {
        headers: {
          'Content-Type': 'application/json',
        },
      };
      // Nutze den Cloudflare Worker für die Registrierung
      const { data } = await axios.post(`${PUBLIC_API_URL}/auth/register`, {
        email,
        password,
        registrationCode,
        userData,
        avatarUrl
      }, config);

      // Auto-login after successful registration and email verification simulation
      if (data) {
        // In a real app, you'd wait for email verification.
        // Here we log in immediately as per simplified requirements.
        await login(email, password);
      }
    } catch (error) {
      if (!error.response || error.response.status !== 409) {
        console.error('Registration failed:', error);
      }
      throw error;
    }
  }, [API_URL, login]);

  const logout = useCallback(() => {
    setUser(null);
    localStorage.removeItem('user');
    localStorage.removeItem('userInfo'); // Auch userInfo entfernen
  }, []);

  const fetchPublicData = useCallback(async (endpoint) => {
    // Try Cloudflare Worker
    try {
      const response = await axios.get(`${PUBLIC_API_URL}${endpoint}`, { timeout: 15000 });
      if (typeof response.data === 'string' && response.data.trim().toLowerCase().startsWith('<!doctype html>')) {
        throw new Error('Received HTML fallback instead of JSON. Check API routing.');
      }
      return response.data;
    } catch (error) {
      console.error(`API failed for ${endpoint}:`, error.message);
      throw error;
    }
  }, [PUBLIC_API_URL]);

  /**
   * Helper to get absolute asset URL for R2 assets.
   */
  const getAssetUrl = useCallback((path, options = {}) => {
    if (!path) return '';
    
    // Resolve path if an object (e.g. { url: '...', src: '...' }) or non-string was passed
    let resolvedPath = path;
    if (typeof resolvedPath === 'object' && resolvedPath !== null) {
      resolvedPath = resolvedPath.url || resolvedPath.src || resolvedPath.path || resolvedPath.key || resolvedPath.modelUrl || '';
    }
    
    if (typeof resolvedPath !== 'string' || !resolvedPath.trim()) {
      return '';
    }

    let absoluteUrl = resolvedPath.trim();
    
    if (!absoluteUrl.startsWith('http') && !absoluteUrl.startsWith('blob:') && !absoluteUrl.startsWith('data:')) {
      // If it already has the correct prefix, just add the PUBLIC_API_URL but without doubling /api
      if (absoluteUrl.startsWith('/api/assets/')) {
        absoluteUrl = `${PUBLIC_API_URL}${absoluteUrl.replace(/^\/api/, '')}`;
      }
      // Fallback for relative paths from legacy backend
      else if (absoluteUrl.startsWith('/uploads/')) {
        absoluteUrl = `${API_URL}${absoluteUrl}`;
      }
      // Detect R2 keys (UUID-style or just filenames that should be in R2)
      else if (!absoluteUrl.startsWith('/') && absoluteUrl.length > 3) {
        absoluteUrl = `${PUBLIC_API_URL}/assets/${absoluteUrl}`;
      }
    }

    // In browser production, ensure absolute origin so image proxy can fetch relative asset paths
    if (typeof absoluteUrl === 'string' && absoluteUrl.startsWith('/') && typeof window !== 'undefined' && window.location && window.location.origin) {
      if (!window.location.hostname.includes('localhost') && !window.location.hostname.includes('127.0.0.1')) {
        absoluteUrl = `${window.location.origin}${absoluteUrl}`;
      }
    }

    // Safety check for formats or origins that should not be proxied
    const isSvg = typeof absoluteUrl === 'string' && absoluteUrl.toLowerCase().includes('.svg');
    const isBlobOrData = typeof absoluteUrl === 'string' && (absoluteUrl.startsWith('data:') || absoluteUrl.startsWith('blob:'));
    const isLocal = typeof absoluteUrl === 'string' && (absoluteUrl.includes('localhost') || absoluteUrl.includes('127.0.0.1') || absoluteUrl.startsWith('/'));
    const isAlreadyProxied = typeof absoluteUrl === 'string' && absoluteUrl.includes('wsrv.nl');

    if (isSvg || isBlobOrData || isLocal || isAlreadyProxied || options?.raw) {
      return absoluteUrl;
    }

    // Apply wsrv.nl image proxy for high-performance WebP compression, resizing & CDN caching
    try {
      const proxyUrl = new URL('https://wsrv.nl/');
      proxyUrl.searchParams.append('url', absoluteUrl);
      
      const width = options?.width || 1200;
      const quality = options?.quality || 80;
      const format = options?.format || 'webp';

      proxyUrl.searchParams.append('w', String(Math.round(width)));
      proxyUrl.searchParams.append('q', String(quality));
      if (options?.height) proxyUrl.searchParams.append('h', String(Math.round(options.height)));
      if (options?.fit) proxyUrl.searchParams.append('fit', String(options.fit));
      proxyUrl.searchParams.append('output', format);

      return proxyUrl.toString();
    } catch (e) {
      return absoluteUrl;
    }

    return absoluteUrl;
  }, [PUBLIC_API_URL, API_URL]);

  if (loading) {
    return <div>Lädt...</div>; // Oder einen Lade-Spinner anzeigen
  }

  return (
    <AuthContext.Provider value={{ 
      user, login, registerUser, logout, googleLogin, 
      API_URL, PUBLIC_API_URL, fetchPublicData, getAssetUrl,
      bobContext, setBobContext
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
